import { useTranslation } from 'react-i18next';

import { renderTrendIcon, TrendIconEnum } from '@/components/atoms/TrendIcon/TrendIconUtils';
import { MetricSummaryRow } from '@/components/molecules/MetricSummaryRow';
import type { DomainCageOverviewPeriod } from '@/domain/cages/overview.types';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { formatNumberByLocale } from '@/utils/localeFormatters';

import styles from './WeightAndGrowthSummary.module.scss';

const RATIO_DECIMALS = 2;
const NO_VALUE = '-';
/** Any movement at all earns the condition factor an arrow. */
const CF_GROWTH_TREND_THRESHOLD = 0;

export interface WeightAndGrowthSummaryProps {
    period: DomainCageOverviewPeriod;
    /**
     * `grid` puts the ratios two by two with the condition-factor explanation under them, for a
     * side panel. `row` puts all four on one line with no explanation, for a card that leads
     * with other metrics — the cage dashboard's weight card.
     */
    layout?: 'grid' | 'row';
    /** Unit the period weights are expressed in, for the CF explanation. */
    weightUnitTag: string;
    isLoading: boolean;
}

/**
 * Period aggregates for a cage, in the label-over-value rows the weight pages use.
 * Shown on the weight dashboard (`grid`) and on the cage dashboard's weight card (`row`).
 */
export default function WeightAndGrowthSummary(props: WeightAndGrowthSummaryProps) {
    const { period, weightUnitTag, isLoading, layout = 'grid' } = props;
    const isRow = layout === 'row';
    const { t, i18n } = useTranslation();

    function formatRatio(value: number | null): string {
        // `Math.abs` matches the previous presentation: these ratios are reported
        // as magnitudes, and a negative RGI rendered with its sign read as an error.
        return value == null
            ? NO_VALUE
            : formatNumberByLocale(Math.abs(value), i18n.language, RATIO_DECIMALS, NO_VALUE);
    }

    /**
     * Reproduces what `WeightStatus` showed: an up arrow whenever the condition
     * factor moved at all, coloured through the reversed palette so growth in CF
     * reads as green rather than as a warning.
     */
    const conditionFactorTrend =
        period.cfGrowth != null && Math.abs(period.cfGrowth) > CF_GROWTH_TREND_THRESHOLD
            ? renderTrendIcon(TrendIconEnum.TrendNegativeUp, true)
            : undefined;

    const conditionFactorExplanation =
        period.weight != null && period.expectedCf != null
            ? t('cageDashboard.weightAndGrowthSummary.kFactorDetails', {
                  weight: formatNumberByLocale(period.weight, i18n.language, 0, NO_VALUE),
                  weightUnit: weightUnitTag,
                  expectedKFactor: formatRatio(period.expectedCf),
              })
            : '';

    return (
        <>
            {/* `CV`, `SGR` and `RGI` are locale-invariant acronyms, so they stay
                literal here as they were in `WeightStatus`. */}
            <GridLayout
                contentLayout={isRow ? '4' : '2'}
                responsive={!isRow}
                justifyItems={isRow ? 'center' : undefined}>
                <MetricSummaryRow
                    label="CV"
                    value={period.cv == null ? NO_VALUE : `${formatRatio(period.cv)} %`}
                    isLoading={isLoading}
                    tooltip={t('descriptions.weightAndGrowthCV')}
                />
                <MetricSummaryRow
                    label="SGR"
                    value={period.sgr == null ? NO_VALUE : `${formatRatio(period.sgr)} %`}
                    isLoading={isLoading}
                    tooltip={t('descriptions.weightAndGrowthSGR')}
                />
                <MetricSummaryRow
                    label="RGI"
                    value={formatRatio(period.rgi)}
                    isLoading={isLoading}
                    tooltip={t('descriptions.weightAndGrowthRGI')}
                />
                <MetricSummaryRow
                    label={t('cageDashboard.weightAndGrowthSummary.kFactor')}
                    value={formatRatio(period.cf)}
                    trend={conditionFactorTrend}
                    isLoading={isLoading}
                    tooltip={t('descriptions.weightAndGrowthK')}
                />
            </GridLayout>
            {!isRow && !isLoading && conditionFactorExplanation && (
                <div className={styles.footnote}>{conditionFactorExplanation}</div>
            )}
        </>
    );
}
